import React, {
	useCallback,
	useMemo,
	useEffect,
	useRef,
	useState,
	type PropsWithChildren,
} from 'react';
import { createPortal } from 'react-dom';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import {
	graphql,
	usePaginationFragment,
	usePreloadedQuery,
	useRefetchableFragment,
	useFragment,
	type EntryPointProps,
} from 'react-relay';
import { v4 as uuid } from 'uuid';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import { Box, xcss } from '@atlaskit/primitives';
import { JiraFieldSetViewAri } from '@atlassian/ari/jira/field-set-view';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService, toFlagId } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import type { CreatedIssues } from '@atlassian/jira-issue-create/src/common/types/index.tsx';
import { getIssueDiscoveryUrl } from '@atlassian/jira-issue-create/src/common/utils/issue-discovery/index.tsx';
import SuccessFlagWithContext from '@atlassian/jira-issue-create/src/ui/modal/success-flag/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';

import { IssueContext } from '@atlassian/jira-issue-field-optional-editing-state-manager/src/index.tsx';
import { HIDE_DONE_SUBTASKS_FILTER } from '@atlassian/jira-issue-shared-types/src/common/types/user-preferences-type.tsx';
import { ColumnPickerContainer } from '@atlassian/jira-issue-table-column-picker/src/controllers/column-picker-open-state/index.tsx';
import {
	useUserPreferencesActions,
	useUserPreferencesValue,
} from '@atlassian/jira-issue-user-preference-services/src/main.tsx';
import type {
	AsyncLoadOptions,
	SelectValue,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import {
	HEADER_HEIGHT,
	REORDER_COLUMNS,
	ROW_HEIGHT,
	SORT_ASC,
	SORT_DESC,
} from '@atlassian/jira-native-issue-table/src/common/constants.tsx';
import type {
	ColumnConfigChangeType,
	SortOrder,
} from '@atlassian/jira-native-issue-table/src/common/types.tsx';
import NativeIssueTable from '@atlassian/jira-native-issue-table/src/ui/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { useSelector } from '@atlassian/jira-react-redux/src/index.tsx';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import ConfigurableChildIssuesPanelWithFragmentQuery, {
	type ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragmentQuery,
} from '@atlassian/jira-relay/src/__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragmentQuery.graphql.ts';
import type { ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_IssueSearchPaginationQuery } from '@atlassian/jira-relay/src/__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_IssueSearchPaginationQuery.graphql.ts';
import type { ui_main_ConfigurableChildIssuesPanelQuery } from '@atlassian/jira-relay/src/__generated__/ui_main_ConfigurableChildIssuesPanelQuery.graphql.ts';
import type { ui_main_ConfigurableChildIssuesPanelWithEntryPointQuery } from '@atlassian/jira-relay/src/__generated__/ui_main_ConfigurableChildIssuesPanelWithEntryPointQuery.graphql.ts';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_rootRelayFragment$key } from '@atlassian/jira-relay/src/__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_rootRelayFragment.graphql';
import type { ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data$key } from '@atlassian/jira-relay/src/__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data.graphql';
import type { ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_childIssuesPanelData$key } from '@atlassian/jira-relay/src/__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_childIssuesPanelData.graphql';
import type { ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_fieldSetViewQueryByProject$key } from '@atlassian/jira-relay/src/__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_fieldSetViewQueryByProject.graphql';
import type { ui_issueViewCommonViews_ConfigurableChildIssuesPanel_RankFieldRootRelayFragment$key } from '@atlassian/jira-relay/src/__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanel_RankFieldRootRelayFragment.graphql';
import { useColumnLoader } from '@atlassian/jira-issue-view-child-issues-panel/src/services/use-column-loader/index.tsx';
import { useColumnResize } from '@atlassian/jira-issue-view-child-issues-panel/src/services/use-column-resize/index.tsx';
import { useColumnsChange } from '@atlassian/jira-issue-view-child-issues-panel/src/services/use-columns-change/index.tsx';
import { useColumnReset } from '@atlassian/jira-issue-view-child-issues-panel/src/services/use-column-reset/index.tsx';
import { useIssuesRanker } from '@atlassian/jira-issue-view-child-issues-panel/src/services/use-issues-ranker/index.tsx';
import {
	ColumnPicker,
	type ColumnPickerApi,
} from '@atlassian/jira-issue-view-child-issues-panel/src/ui/column-picker/index.tsx';
import withContainerWidth from '@atlassian/jira-issue-view-common-utils/src/with-container-width/index.tsx';
import type { Value } from '@atlassian/jira-issue-user-preference-services/src/types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	CHILD_ISSUES_PAGE_SIZE,
	DEFAULT_FIELD_SET_VIEW_ID,
	SORT_OPTION,
} from '@atlassian/jira-issue-view-child-issues-panel/src/common/constants.tsx';
import { useChildIssuesSortBy } from '../../../../controllers/main.tsx';
import {
	getIsClassicSubtaskPanel,
	getIsChildrenIssuesPanel,
	getIsSimplifiedProject,
} from '../../../../state/context/selectors.tsx';
import {
	getRecentlyCreatedIssue,
	getRecentlyLinkedChildIssue,
} from '../../../../state/entities/selectors.tsx';
import type { CreatedIssue } from '../../../../state/entities/types.tsx';
import { useRefetchWithoutSuspend } from '../services/use-refetch-without-suspend/index.tsx';
import { useEventHandlers } from '../utils/use-event-handlers.tsx';

import {
	JQLBuilder,
	createConfigurableChildIssuesPanelQueryVariables,
	getColumnConfigOverrides,
	onChildIssueLoadErrorFlag,
} from '../utils/index.tsx';
import type { ColumnId, ConfigurableChildIssuesPanelProps } from './types.tsx';

const CHIN_TABLE_MIN_WIDTH = 480;

export const ConfigurableChildIssuesPanelWithEntryPoint = ({
	props,
	queries: { childIssuesData },
}: EntryPointProps<
	{ childIssuesData: ui_main_ConfigurableChildIssuesPanelWithEntryPointQuery },
	{},
	ConfigurableChildIssuesPanelProps & { rankFieldJql: string },
	{}
>) => {
	const data = usePreloadedQuery<ui_main_ConfigurableChildIssuesPanelWithEntryPointQuery>(
		graphql`
			query ui_main_ConfigurableChildIssuesPanelWithEntryPointQuery(
				$cloudId: ID!
				$issueSearchInput: JiraIssueSearchInput!
				$first: Int
				$last: Int
				$before: String
				$after: String
				$namespace: String
				$viewId: String
				$filterId: String
				$fieldSetIds: [String!]!
				$amountOfColumns: Int!
				$shouldQueryFieldSetsById: Boolean!
				$fieldSetsInput: JiraIssueSearchFieldSetsInput
				$fieldSetsContext: JiraIssueSearchViewFieldSetsContext
				$issueKey: String!
			) {
				...ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data
			}
		`,
		childIssuesData,
	);

	return <ConfigurableChildIssuesPanelWithFragmentWithContainerWidth {...props} data={data} />;
};

export const ConfigurableChildIssuesPanel = ({
	projectId,
	parentIssueTypeId,
	filterSubtasks,
	rootRelayFragment,
	isHideDoneFilterEnabled,
	...props
}: ConfigurableChildIssuesPanelProps) => {
	const cloudId = useCloudId();
	const issueKey = useIssueKey();
	const [, { getUserPreferenceValue }] = useUserPreferencesActions();
	const hideDone: Value =
		isHideDoneFilterEnabled && fg('key-chin-hide-done-relay-migration')
			? isHideDoneFilterEnabled.toString()
			: // eslint-disable-next-line react-hooks/rules-of-hooks
				useMemo(() => getUserPreferenceValue(HIDE_DONE_SUBTASKS_FILTER), [getUserPreferenceValue]);

	const rankFieldJql =
		useFragment<ui_issueViewCommonViews_ConfigurableChildIssuesPanel_RankFieldRootRelayFragment$key>(
			graphql`
				fragment ui_issueViewCommonViews_ConfigurableChildIssuesPanel_RankFieldRootRelayFragment on JiraQuery {
					rankField(cloudId: $cloudId) {
						jqlTerm
					}
				}
			`,
			rootRelayFragment ? rootRelayFragment.jira : null,
		)?.rankField?.jqlTerm || 'created';

	const data = usePreloadedQuery<ui_main_ConfigurableChildIssuesPanelQuery>(
		graphql`
			query ui_main_ConfigurableChildIssuesPanelQuery(
				$cloudId: ID!
				$issueSearchInput: JiraIssueSearchInput!
				$first: Int
				$last: Int
				$before: String
				$after: String
				$namespace: String
				$viewId: String
				$filterId: String
				$fieldSetIds: [String!]!
				$amountOfColumns: Int!
				$shouldQueryFieldSetsById: Boolean!
				$fieldSetsInput: JiraIssueSearchFieldSetsInput
				$fieldSetsContext: JiraIssueSearchViewFieldSetsContext
				$issueKey: String!
			) {
				...ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data
			}
		`,
		{
			kind: 'PreloadedQuery',
			environment: getRelayEnvironment(),
			fetchKey: 'issue',
			fetchPolicy: 'store-or-network',
			name: 'issueViewCommon',
			variables: createConfigurableChildIssuesPanelQueryVariables({
				cloudId,
				jql: JQLBuilder({
					issueKey,
					hideDone,
					sortColumn: rankFieldJql,
					sortDirection: SORT_ASC,
					filterSubtasks,
				}),
				fieldSetIds: [],
				issueKey,
			}),
			dispose: noop,
			isDisposed: false,
		},
	);

	return (
		<ConfigurableChildIssuesPanelWithFragmentWithContainerWidth
			projectId={projectId}
			parentIssueTypeId={parentIssueTypeId}
			data={data}
			rootRelayFragment={rootRelayFragment}
			filterSubtasks={filterSubtasks}
			rankFieldJql={rankFieldJql}
			isHideDoneFilterEnabled={isHideDoneFilterEnabled}
			{...props}
		/>
	);
};

type ConfigurableChildIssuesPanelWithFragmentProps = {
	data: ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data$key;
	rankFieldJql: string;
};

export const ConfigurableChildIssuesPanelWithFragment = ({
	data,
	filterSubtasks,
	rootRelayFragment,
	rankFieldJql,
	containerWidth,
	columnPickerRef,
	isHideDoneFilterEnabled,
}: ConfigurableChildIssuesPanelProps & ConfigurableChildIssuesPanelWithFragmentProps) => {
	const cloudId = useCloudId();
	const issueKey = useIssueKey();
	const [reduxHideDone] = useUserPreferencesValue(HIDE_DONE_SUBTASKS_FILTER);
	const hideDone =
		isHideDoneFilterEnabled && fg('key-chin-hide-done-relay-migration')
			? isHideDoneFilterEnabled.toString()
			: reduxHideDone;
	const [sortByValue] = useChildIssuesSortBy();
	const [columnNames, setColumnNames] = useState<ColumnId[]>([]);
	const [sortDirection, setSortDirection] = useState<SortOrder>(SORT_ASC);
	const [sortColumn, setSortColumn] = useState<ColumnId>(rankFieldJql);
	const firstUpdate = useRef(true);
	const { showFlag } = useFlagsService();

	const eventHandlers = useEventHandlers(issueKey);

	const columnPickerApi = useRef<ColumnPickerApi>(null);
	// TODO: Remove on `jira-nike-chin-config-button-in-header` cleanup in favour of columnPickerApi
	const resetRef = useRef<HTMLDivElement>(null);

	if (fg('issue-jiv-19838-chin-api-lookup-through-issue-key')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useFragment<ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_rootRelayFragment$key>(
			graphql`
				fragment ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_rootRelayFragment on Query
				@argumentDefinitions(
					isIssueSearchAPIEnabled: {
						type: "Boolean!"
						provider: "@atlassian/jira-relay-provider/src/jira-issue-view-chin-m2-performance-isissuesearchapienabled.relayprovider"
					}
				) {
					jira @required(action: THROW) {
						# eslint-disable-next-line @atlassian/relay/unused-fields
						issueSearch(
							cloudId: $cloudId
							issueSearchInput: $issueSearchInput
							fieldSetsInput: $fieldSetsInput
						) @optIn(to: "JiraSpreadsheetComponent-M1") @include(if: $isIssueSearchAPIEnabled) {
							...ui_nativeIssueTable_NativeIssueTable_issues
							__id
							totalCount
							errors {
								message
							}
						}
						fieldSetViewQueryByProject(cloudId: $cloudId, issueKey: $issueKey)
							@required(action: THROW)
							@optIn(to: "JiraIssueSearch") {
							... on JiraFieldSetView {
								__typename
								fieldSets(first: $amountOfColumns, filter: { fieldSetSelectedState: SELECTED })
									@required(action: THROW) {
									__id
									...ui_nativeIssueTable_NativeIssueTable_columns
								}
							}
							... on QueryError {
								__typename
							}
						}
					}
				}
			`,
			rootRelayFragment,
		);
	}

	const newJQL = useMemo(
		() => JQLBuilder({ issueKey, hideDone, sortColumn, sortDirection, filterSubtasks }),
		[issueKey, hideDone, sortColumn, sortDirection, filterSubtasks],
	);

	const queryVariables = useMemo(
		() =>
			createConfigurableChildIssuesPanelQueryVariables({
				cloudId,
				jql: newJQL,
				fieldSetIds: columnNames,
				issueKey,
			}),
		// Don't update attributes when newJQL changes
		// The refetch is triggered manually and without suspense
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[cloudId, columnNames, issueKey],
	);

	const [childIssuesPanelData, refetch] = useRefetchableFragment<
		ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragmentQuery,
		ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data$key
	>(
		graphql`
			fragment ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data on Query
			@argumentDefinitions(
				isIssueSearchAPIEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-issue-view-chin-m2-performance-isissuesearchapienabled.relayprovider"
				}
				isRealtimeUpdateEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-issue-view-child-issues-panel-realtime-update.relayprovider"
				}
			)
			@refetchable(
				queryName: "ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragmentQuery"
			) {
				jira @required(action: THROW) {
					issueByKey(cloudId: $cloudId, key: $issueKey) @include(if: $isRealtimeUpdateEnabled) {
						projectField {
							project {
								...ui_nativeIssueTable_NativeIssueTable_project
							}
						}
					}
					issueSearch(
						cloudId: $cloudId
						issueSearchInput: $issueSearchInput
						first: $first
						last: $last
						before: $before
						after: $after
						fieldSetsInput: $fieldSetsInput
					) @optIn(to: "JiraSpreadsheetComponent-M1") @include(if: $isIssueSearchAPIEnabled) {
						__id
						totalCount
						...ui_nativeIssueTable_NativeIssueTable_issues
						issueSearchError {
							__typename
						}
					}
					issueSearchStable(
						cloudId: $cloudId
						issueSearchInput: $issueSearchInput
						fieldSetsInput: $fieldSetsInput
					) @skip(if: $isIssueSearchAPIEnabled) {
						__id
						totalCount
						...ui_nativeIssueTable_NativeIssueTable_issues
						issueSearchError {
							__typename
						}
					}
					fieldSetViewQueryByProject(cloudId: $cloudId, issueKey: $issueKey)
						@optIn(to: "JiraIssueSearch") {
						... on JiraFieldSetView {
							__typename
							id
							hasDefaultFieldSets
							fieldSets(first: $amountOfColumns, filter: { fieldSetSelectedState: SELECTED }) {
								__id
								...ui_nativeIssueTable_NativeIssueTable_columns
								...columnPicker_issueViewChildIssuesPanel
							}
							...ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_fieldSetViewQueryByProject
								@arguments(amountOfColumns: $amountOfColumns)
						}
						... on QueryError {
							__typename
						}
					}
				}
				...ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_childIssuesPanelData
			}
		`,
		data,
	);

	const {
		data: issueSearchPaginatedData,
		hasNext,
		hasPrevious,
		isLoadingNext,
		isLoadingPrevious,
		loadNext,
		loadPrevious,
	} = usePaginationFragment<
		ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_IssueSearchPaginationQuery,
		ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_childIssuesPanelData$key
	>(
		graphql`
			fragment ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_childIssuesPanelData on Query
			@argumentDefinitions(
				isIssueSearchAPIEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-issue-view-chin-m2-performance-isissuesearchapienabled.relayprovider"
				}
			)
			@refetchable(
				queryName: "ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_IssueSearchPaginationQuery"
			) {
				jira @required(action: THROW) {
					issueSearch(
						cloudId: $cloudId
						issueSearchInput: $issueSearchInput
						first: $first
						last: $last
						before: $before
						after: $after
						fieldSetsInput: $fieldSetsInput
					)
						@connection(
							key: "ChildIssuesPanelIssueSearchPagination__issueSearch"
							filters: ["issueSearchInput"]
						)
						@optIn(to: "JiraSpreadsheetComponent-M1")
						@include(if: $isIssueSearchAPIEnabled) {
						__id
						# eslint-disable-next-line @atlassian/relay/unused-fields
						edges {
							cursor
						}
						pageInfo {
							endCursor
							startCursor
						}
						totalCount
						issueSearchError {
							__typename
						}
						...ui_nativeIssueTable_NativeIssueTable_issues
					}
				}
			}
		`,
		childIssuesPanelData,
	);

	const {
		issueByKey,
		issueSearchStable,
		fieldSetViewQueryByProject,
		issueSearch: issueSearchNonPaginated,
	} = childIssuesPanelData.jira;

	const fieldSetViewMetadata =
		useFragment<ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_fieldSetViewQueryByProject$key>(
			graphql`
				fragment ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_fieldSetViewQueryByProject on JiraFieldSetView
				@argumentDefinitions(amountOfColumns: { type: "Int!" }) {
					id
					fieldSets(first: $amountOfColumns, filter: { fieldSetSelectedState: SELECTED }) {
						__id
						...ui_nativeIssueTable_NativeIssueTable_columns
						...columnPicker_issueViewChildIssuesPanel
					}
				}
			`,
			fieldSetViewQueryByProject?.__typename === 'JiraFieldSetView'
				? fieldSetViewQueryByProject
				: null,
		);

	const onError = useCallback(
		(error: Error) => {
			showFlag(onChildIssueLoadErrorFlag);
			fireErrorAnalytics({
				meta: {
					id: 'loadConfigurableChildIssueTableError',
					packageName: 'jiraIssueViewCommonViews',
					teamName: 'bento',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
				skipSentry: true,
			});
		},
		[showFlag],
	);

	const issueSearch = fg('jira-issue-view-child-issues-panel-pagination')
		? issueSearchPaginatedData.jira.issueSearch
		: issueSearchNonPaginated;

	const issues = expVal('jira-issue-view-chin-m2-performance', 'isIssueSearchAPIEnabled', false)
		? issueSearch
		: issueSearchStable;

	if (issues?.issueSearchError || fieldSetViewQueryByProject?.__typename === 'QueryError') {
		onError(new Error('child issue search GET API error'));
	}

	const fieldSetViewIdOld =
		fieldSetViewQueryByProject?.__typename === 'JiraFieldSetView'
			? fieldSetViewQueryByProject?.id
			: null;

	const fieldSetViewId = fg('issue-jiv-20390-column-picker-disappearing-fix')
		? fieldSetViewMetadata?.id ?? null
		: fieldSetViewIdOld;

	const fieldSetsOld =
		fieldSetViewQueryByProject?.__typename === 'JiraFieldSetView'
			? fieldSetViewQueryByProject?.fieldSets
			: null;

	const fieldSets = fg('issue-jiv-20390-column-picker-disappearing-fix')
		? fieldSetViewMetadata?.fieldSets ?? null
		: fieldSetsOld;

	const hasDefaultFieldSets =
		fieldSetViewQueryByProject?.__typename === 'JiraFieldSetView'
			? fieldSetViewQueryByProject?.hasDefaultFieldSets
			: null;

	/**
	 * By default, refetching a Relay refetchable fragment will suspend a component,
	 * so we have a hook tied in with the loading state that fetches without suspending
	 * and disable interactivity with the old result set. In React 18 the workaround
	 * will no longer be needed as any refetch operation inside a transition will not suspend the component.
	 */
	// TODO: Remove when React18 is here
	const { isLoading, refetchWithoutSuspending } = useRefetchWithoutSuspend({
		defaultVariables: queryVariables,
		refetch,
		query: ConfigurableChildIssuesPanelWithFragmentQuery,
		onError,
	});

	const refetchChildIssues = useCallback(() => {
		refetchWithoutSuspending(
			{
				issueSearchInput: {
					jql: newJQL,
				},
				fieldSetIds: columnNames,
			},
			{
				fetchPolicy: 'store-or-network',
			},
		);
	}, [columnNames, newJQL, refetchWithoutSuspending]);

	/**
	 * When the JQL changes (except for the first update) then refetch data
	 */
	useEffect(() => {
		if (firstUpdate.current) {
			firstUpdate.current = false;
			return;
		}
		refetchChildIssues();
	}, [newJQL, refetchChildIssues]);

	const recentlyCreatedIssue = useSelector(getRecentlyCreatedIssue);
	const previousCreatedIssueKey = usePrevious(recentlyCreatedIssue?.issueKey);

	const recentlyLinkedChildIssue = useSelector(getRecentlyLinkedChildIssue);
	const previousLinkedChildIssueKey = usePrevious(recentlyLinkedChildIssue?.issueKey);

	const onRecentlyIssueCreationSuccess = useCallback(
		(issue: CreatedIssue) => {
			if (!issue.usedGlobalCreateDialog) {
				const flagId = toFlagId(uuid());
				const issueList: CreatedIssues = [
					{
						issueId: Number(issue.issueId),
						issueKey: issue.issueKey,
					},
				];
				const issueDiscoveryUrl = getIssueDiscoveryUrl(issueList);
				showFlag({
					id: flagId,
					render: (flagProps) => {
						const successFlagProps = {
							key: flagProps.key,
							flagKey: flagProps.key,
							id: flagId,
							issueList,
							issueDiscoveryUrl,
						};
						return <SuccessFlagWithContext {...successFlagProps} />;
					},
				});
			}
		},
		[showFlag],
	);

	/**
	 * When a newly created/linked child issue is detected, refetch the table
	 * and if the issue is successfully created show a success flag
	 */
	useEffect(() => {
		const hasRecentlyCreatedIssue = previousCreatedIssueKey !== recentlyCreatedIssue?.issueKey;
		const hasRecentlyLinkedIssue =
			previousLinkedChildIssueKey !== recentlyLinkedChildIssue?.issueKey;
		if (hasRecentlyCreatedIssue || hasRecentlyLinkedIssue) {
			if (hasRecentlyCreatedIssue && recentlyCreatedIssue) {
				onRecentlyIssueCreationSuccess(recentlyCreatedIssue);
			}
			refetchChildIssues();
		}
	}, [
		previousCreatedIssueKey,
		previousLinkedChildIssueKey,
		recentlyCreatedIssue?.issueKey,
		recentlyLinkedChildIssue?.issueKey,
		refetchChildIssues,
		onRecentlyIssueCreationSuccess,
		recentlyCreatedIssue,
	]);

	/**
	 * When the OrderBy value (sortByValue) changes
	 * then set the sorting accordingly
	 */
	useEffect(() => {
		setSortDirection(SORT_ASC);
		if (!sortByValue || sortByValue === SORT_OPTION.RANKING) {
			setSortColumn(rankFieldJql);
		} else {
			setSortColumn(sortByValue);
		}
	}, [sortByValue, rankFieldJql]);

	/**
	 * Establish context for this panel based on the data
	 */
	const hasMultipleIssues = Number(issues?.totalCount ?? 0) > 1;
	const isSimplifiedProject = useSelector(getIsSimplifiedProject);
	const isChildrenIssuesPanel = useSelector(getIsChildrenIssuesPanel);
	const isClassicSubtaskPanel = useSelector(getIsClassicSubtaskPanel);

	/**
	 * onSortChange is invoked when the sorting criteria is updated by the user
	 * The issue table will display sorting indicators and tooltips when hovering over a table header (i.e. column name)
	 */
	const onSortChange = useCallback((field: ColumnId, direction: SortOrder) => {
		setSortDirection(direction === SORT_ASC ? SORT_ASC : SORT_DESC);
		setSortColumn(field);
	}, []);

	/**
	 * Determines whether a field set view has already been created
	 * i.e., user has changed column configuration (resize, reorder) in a project
	 */
	const isFieldSetViewCreated =
		fieldSetViewId != null &&
		// Should be okay to parse since `fieldSetViewId` from the ARI is a number
		// See https://developer.atlassian.com/platform/atlassian-resource-identifier/resource-owners/registry/#ati%3Acloud%3Ajira%3Afield-set-view
		Number(JiraFieldSetViewAri.parse(fieldSetViewId).fieldSetViewId) !== DEFAULT_FIELD_SET_VIEW_ID;

	const [onColumnsChange, isColumnsChangeInFlight] = useColumnsChange({
		fieldSetViewId,
		fieldSetsConnectionId: fieldSets?.__id ?? '',
		onCompleted: (
			changeType: ColumnConfigChangeType,
			success: boolean | undefined,
			columnIds: ColumnId[],
		) => {
			if (success && changeType !== REORDER_COLUMNS) {
				setColumnNames(columnIds);
			}
		},
	});

	const onRestoreDefaultColumns = useColumnReset({
		fieldSetViewId,
		onSuccess: () => {
			if (fg('jira-nike-chin-config-button-in-header')) {
				columnPickerApi.current?.close();
			} else {
				resetRef.current?.click();
			}
			refetchChildIssues();
		},
	});
	/**
	 * onColumnResize is invoked after a user has completed a resize interaction with a column
	 */
	const [onColumnResize, isColumnResizeInFlight] = useColumnResize({
		fieldSetsId: fieldSets?.__id ?? '',
	});

	/**
	 * onRowReorder is invoked after a user reorders issues via a drag-and-drop operation.
	 *
	 * This callback will only be passed if `canReorderIssues` is true
	 */
	const onRowReorder = useIssuesRanker(issues?.__id || '');

	/**
	 * Determines whether a user can reorder issues
	 */
	const canReorderIssues =
		// Issue ranking is only supported in CMP Subtasks and TMP Child Issues
		(isClassicSubtaskPanel || (isSimplifiedProject && isChildrenIssuesPanel)) &&
		// All issues should be visible
		hideDone !== 'true' &&
		// Should be at least 2 issues
		hasMultipleIssues &&
		// Rank field should be the sort option
		sortColumn === rankFieldJql;

	/**
	 * useColumnLoaderFromIssueKey is invoked when options need to be retrieved for the column picker
	 */
	const useColumnLoaderFromIssueKey: (selectedColumns: SelectValue) => AsyncLoadOptions = (
		selectedColumns: SelectValue,
	) => {
		return useColumnLoader({ issueKey, selectedColumns });
	};

	const isAnonymous = useIsAnonymous();

	/**
	 * Blocks the usage of the column picker
	 */
	const isColumnPickerLoading =
		// When column size and column change mutation is in flight
		(isColumnResizeInFlight || isColumnsChangeInFlight) &&
		// and the field set view is NOT yet created
		!isFieldSetViewCreated &&
		fg('issue-jiv-20390-column-picker-disappearing-fix');

	if (!fieldSets) return null;
	if (!issues || !issues.totalCount) {
		fireErrorAnalytics({
			meta: {
				id: 'loadConfigurableChildIssueTableError',
				packageName: 'jiraIssueViewCommonViews',
				teamName: 'bento',
			},
			error: new Error('issue total count is 0'),
			attributes: {
				isIssueSearch: expVal(
					'jira-issue-view-chin-m2-performance',
					'isIssueSearchAPIEnabled',
					false,
				),
				isBEPerfEnabled: fg('jira-issue-view-child-issues-panel-performance'),
			},
		});
	}

	return (
		<>
			{columnPickerRef.current != null &&
				!isAnonymous &&
				fg('jira-nike-chin-config-button-in-header') &&
				createPortal(
					<ColumnPickerContainer>
						<ColumnPicker
							columns={fieldSets}
							isLoading={isColumnPickerLoading}
							ref={columnPickerApi}
							onColumnsChange={onColumnsChange}
							onRestoreDefaultColumns={!hasDefaultFieldSets ? onRestoreDefaultColumns : undefined}
							useColumnLoaderProp={useColumnLoaderFromIssueKey}
							{...(expVal('jira_list_merged_column', 'isEnabled', false) && {
								isMergedColumnEnabled: true,
							})}
							{...(expVal('visual_uplift_for_child_issues_table', 'isEnabled', false) && {
								isVisualUpliftForChildIssuesTableExpEnabled: true,
							})}
						/>
					</ColumnPickerContainer>,
					columnPickerRef.current,
				)}
			{!!(containerWidth && containerWidth > 0) && !!issues?.totalCount && (
				<IssueContext.Provider value="localStateMode">
					<Box ref={resetRef} />
					<MaybeIssueTableScrollViewport>
						<IssueTableContainer>
							<NativeIssueTable
								project={
									fg('jira-issue-view-child-issues-panel-realtime-update')
										? issueByKey?.projectField?.project
										: undefined
								}
								issues={issues}
								sortField={sortColumn}
								sortDirection={sortDirection}
								columns={fieldSets}
								columnConfigOverrides={getColumnConfigOverrides(containerWidth)}
								onRowReorder={canReorderIssues ? onRowReorder : undefined}
								onSortChange={onSortChange}
								loading={isLoading}
								{...(!isAnonymous && {
									onColumnResize,
									onColumnsChange,
								})}
								{...(!isAnonymous &&
									!hasDefaultFieldSets && {
										onRestoreDefaultColumns,
									})}
								{...(!isAnonymous &&
									!fg('jira-nike-chin-config-button-in-header') && {
										useColumnLoader: useColumnLoaderFromIssueKey,
									})}
								isColumnPickerLoading={isColumnPickerLoading}
								isIssueOperationMeatballMenuEnabled={false}
								isCompactColumnEnabled
								isEntireRowDraggable
								{...(fg('jira-issue-view-child-issues-panel-realtime-update') && {
									isStatusTrackingEnabled: true,
								})}
								{...(fg('jira-issue-view-child-issues-panel-pagination') && {
									// TODO: Smarter threshold https://jplat.atlassian.net/browse/JIV-20800
									activationThreshold: ROW_HEIGHT * 2,
									onLoadNext: () => loadNext(CHILD_ISSUES_PAGE_SIZE),
									onLoadPrevious: () => loadPrevious(CHILD_ISSUES_PAGE_SIZE),
									isLoadingNext,
									isLoadingPrevious,
									hasNext,
									hasPrevious,
								})}
								{...(expVal('jira_list_merged_column', 'isEnabled', false) && {
									isMergedColumnEnabled: true,
								})}
								{...(expVal('visual_uplift_for_child_issues_table', 'isEnabled', false) && {
									isVisualUpliftForChildIssuesTableExpEnabled: true,
								})}
								components={{
									columnPicker: {
										Icon: CustomizeIcon,
									},
								}}
								isColumnBordersEnabled={false}
								eventHandlers={eventHandlers}
							/>
						</IssueTableContainer>
					</MaybeIssueTableScrollViewport>
				</IssueContext.Provider>
			)}
		</>
	);
};

// Exported for testing

export const ConfigurableChildIssuesPanelWithFragmentWithContainerWidth = withContainerWidth(
	ConfigurableChildIssuesPanelWithFragment,
);
export default ConfigurableChildIssuesPanel;

const IssueTableScrollViewport = ({ children }: PropsWithChildren<{}>) => {
	const maxHeight = HEADER_HEIGHT + ROW_HEIGHT * CHILD_ISSUES_PAGE_SIZE;

	return (
		<Box
			xcss={scrollViewportStyles}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{ maxHeight: `${maxHeight}px` }}
		>
			{children}
		</Box>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IssueTableViewPort = styled.div({
	overflow: 'auto',
});

const MaybeIssueTableScrollViewport = componentWithFG(
	'jira-issue-view-child-issues-panel-pagination',
	IssueTableScrollViewport,
	IssueTableViewPort,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledIssueTableContainer = styled.div({
	minWidth: `${CHIN_TABLE_MIN_WIDTH}px`,
});

const BasicIssueTableContainer = ({ children }: { children: React.ReactNode }) => <>{children}</>;

const IssueTableContainer = componentWithFG(
	'issue-jiv-20266-remove-double-scrollbars',
	BasicIssueTableContainer,
	StyledIssueTableContainer,
);

const scrollViewportStyles = xcss({
	display: 'flex',
	flexBasis: 0,
	flexDirection: 'column',
	flexGrow: 1,
	overflow: 'hidden',
});
